import React, { useState, useEffect } from 'react';
import { Task, FilterType } from './types/Task';
import { taskService } from './services/taskService';
import TaskList from './components/TaskList';
import TaskInput from './components/TaskInput';
import FilterButtons from './components/FilterButtons';
import { CheckCircle2, ListTodo, Loader2, AlertCircle } from 'lucide-react';
import './styles/App.css';

const STORAGE_KEY = 'taskManager_tasks';

const App: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const savedTasks = localStorage.getItem(STORAGE_KEY);
    if (savedTasks) {
      try {
        setTasks(JSON.parse(savedTasks));
        setLoading(false);
      } catch (err) {
        console.error('Error loading tasks from localStorage:', err);
        fetchTasks();
      }
    } else {
      fetchTasks();
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
    }
  }, [tasks, loading]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTasks = await taskService.getAllTasks();
      setTasks(fetchedTasks);
    } catch (err) {
      setError('Failed to fetch tasks. Using local data.');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (description: string) => {
    try {
      const newTask = await taskService.createTask({ description });
      setTasks([newTask, ...tasks]);
      setError(null);
    } catch (err) {
      const newTask: Task = {
        id: Date.now(),
        description,
        isCompleted: false,
        createdDate: new Date().toISOString(),
      };
      setTasks([newTask, ...tasks]);
      setError('Task created locally. API unavailable.');
      console.error('Error creating task:', err);
    }
  };

  const handleToggleTask = async (id: number) => {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;

    const updatedTasks = tasks.map((t) =>
      t.id === id ? { ...t, isCompleted: !t.isCompleted } : t
    );
    setTasks(updatedTasks);

    try {
      await taskService.toggleTaskCompletion(id, !task.isCompleted);
    } catch (err) {
      console.error('Error toggling task:', err);
      setError('Failed to sync with server. Changes saved locally.');
    }
  };

  const handleDeleteTask = async (id: number) => {
    const updatedTasks = tasks.filter((t) => t.id !== id);
    setTasks(updatedTasks);

    try {
      await taskService.deleteTask(id);
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to sync with server. Changes saved locally.');
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'active') return !task.isCompleted;
    if (filter === 'completed') return task.isCompleted;
    return true;
  });

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.isCompleted).length;
  const activeTasks = totalTasks - completedTasks;

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-icon">
          <ListTodo size={40} />
        </div>
        <h1 className="app-title">Task Manager</h1>
        <p className="app-subtitle">Organize your life, one task at a time ✨</p>
      </div>

      <div className="stats-card">
        <div className="stats-grid">
          <div className="stat-item total">
            <div className="stat-number">{totalTasks}</div>
            <div className="stat-label">Total Tasks</div>
          </div>
          <div className="stat-item completed">
            <div className="stat-number">{completedTasks}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-item active">
            <div className="stat-number">{activeTasks}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="main-card">
        <TaskInput onAddTask={handleAddTask} />
        
        <FilterButtons
          currentFilter={filter}
          onFilterChange={setFilter}
          counts={{ total: totalTasks, active: activeTasks, completed: completedTasks }}
        />

        {loading ? (
          <div className="loading-container">
            <Loader2 size={40} className="loading-spinner" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="empty-state">
            <CheckCircle2 size={80} className="empty-icon" />
            <p className="empty-text">
              {filter === 'completed'
                ? 'No completed tasks yet. Start checking them off! 🎯'
                : filter === 'active'
                ? 'All tasks completed! Great job! 🎉'
                : 'No tasks yet. Add one above to get started! 🚀'}
            </p>
          </div>
        ) : (
          <TaskList
            tasks={filteredTasks}
            onToggleTask={handleToggleTask}
            onDeleteTask={handleDeleteTask}
          />
        )}
      </div>

      <div className="footer">
        <span>Press Enter to add tasks</span>
        <span className="footer-divider">•</span>
        <span>Click checkbox to complete</span>
        <span className="footer-divider">•</span>
        <span>Hover to delete</span>
      </div>
    </div>
  );
};

export default App;